import type { SoftphoneHeaderProps } from './index.types';
import { ReactComponent as ThreeUsersIcon } from 'Assets/icons/three-users.svg';
import { ReactComponent as UserIcon } from 'Assets/icons/user.svg';
import { ReactComponent as PhoneIcon } from 'Assets/images/phone-icon-sidebar.svg';
import { observer } from 'mobx-react';
import { useSoftphoneContact } from 'Modules/softphone/hooks';
import { useConferenceCallManager } from 'Modules/softphone/hooks/useConferenceCallManager';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React from 'react';
import { formatUserInitials } from 'Utils/formatUserInitials';
import { formatNumberWithNationalCode } from 'Utils/phoneUtil';
import { Avatar, AvatarFallback, AvatarImage } from '~/lib/ui/avatar';

/**
 * SoftphoneHeader - Displays contact information in the Softphone
 *
 * Shows:
 * - Avatar with image, initials, or fallback icon
 * - Contact name (or phone number if name is not available)
 * - Formatted phone number below name (only shown when both name and phone exist)
 *
 * Fallback icon logic:
 * - If contact has a name: show initials
 * - If only phone number (no name): show phone icon
 * - If neither: show user icon
 *
 * Gets call data directly from the store and looks up contact/person.
 * Wrapped with observer for MobX reactivity (PersonStore cache).
 */
export const SoftphoneHeader = observer(
  ({ avatarSize, testID }: SoftphoneHeaderProps) => {
    const headerTestId = testID ? `${testID}-header` : undefined;
    const { displayName: name, phoneNumber, image } = useSoftphoneContact();
    const { isConferenceActive } = useConferenceCallManager();

    // Check if the current call is actually part of the conference.
    // When a new 1:1 call is active during a conference, we should show
    // the normal call header, not the conference header.
    const currentCall = useSoftphoneStore((state) =>
      state.currentCallId
        ? state.activeCalls.get(state.currentCallId) ?? null
        : null
    );
    const showConferenceView = isConferenceActive && Boolean(currentCall?.conferenceId);

    const formattedPhone = phoneNumber
      ? formatNumberWithNationalCode(phoneNumber)
      : undefined;

    // Show name if available, otherwise show phone number
    const displayName = showConferenceView
      ? 'Conference call'
      : name || formattedPhone || '';
    const showPhoneBelow = !showConferenceView && name && formattedPhone;
    const hasName = Boolean(name);
    const hasPhoneOnly = !hasName && Boolean(phoneNumber);

    // Determine which fallback to show:
    // - Name available: show initials
    // - Phone only (no name): show phone icon
    // - Neither: show user icon
    // (Conference view is handled separately in the Avatar render)
    const renderFallbackContent = () => {
      if (hasName) {
        return formatUserInitials(name || formattedPhone || '');
      }
      if (hasPhoneOnly) {
        return <PhoneIcon className="size-[40px] text-background" />;
      }
      return <UserIcon className="size-[40px] text-background" />;
    };

    return (
      <div
        className="flex flex-col items-center gap-[12px]"
        data-testid={headerTestId}
      >
        <Avatar className={avatarSize}>
          {showConferenceView ? (
            <div
              className="bg-foreground text-background font-normal text-[30px] flex h-full w-full items-center justify-center rounded-full"
              aria-hidden="true"
            >
              <ThreeUsersIcon className="size-[40px] text-background" />
            </div>
          ) : image ? (
            <>
              <AvatarImage src={image} alt={displayName} />
              <AvatarFallback
                className="bg-foreground text-background font-normal text-[30px]"
                delayMs={1000}
                aria-hidden="true"
              >
                {renderFallbackContent()}
              </AvatarFallback>
            </>
          ) : (
            <div
              className="bg-foreground text-background font-normal text-[30px] flex h-full w-full items-center justify-center rounded-full"
              aria-hidden="true"
            >
              {renderFallbackContent()}
            </div>
          )}
        </Avatar>

        <div className="flex flex-col items-center gap-[4px] w-full px-4">
          <h2 className="max-w-[220px] text-[20px] h-[27px] leading-[27px] font-semibold text-foreground text-center truncate block w-full">
            {displayName}
          </h2>

          {showPhoneBelow && (
            <p className="max-w-[220px] text-[14px] text-foreground text-center truncate block w-full">
              {formattedPhone}
            </p>
          )}
        </div>
      </div>
    );
  }
);
